<template>
    <header class="v-promo__header">
        <div
            class="v-promo__headerTop w-100 d-flex flex-wrap gap-1 align-items-center"
        >
            <div
                v-if="promotion.loyalty"
                v-text="'Tylko z aplikacją'"
                class="a-badge a-badge--success"
            />

            <div
                v-if="promotion.usage_limit_per_loyalty_card"
                v-tooltip="
                    `Promocja ma limit użyć na kartę klienta: ${Number(
                        promotion.usage_limit_per_loyalty_card
                    )}`
                "
                class="a-badge a-badge--success"
            >
                Limit na kartę
                <a-icon icon="Info" />
            </div>

            <div
                v-if="promotion.subscriptions_auto_acceptance"
                v-tooltip="'Promocja aktywowana przez centralę'"
                class="a-badge a-badge--warning"
            >
                Centrala
                <a-icon icon="Info" />
            </div>

            <div v-if="promotion.featured" class="a-badge">
                {{ promotion.featured_text || 'Wyróżniona' }}
            </div>

            <div class="v-promo__dates d-flex flex-wrap">
                <div class="d-flex align-items-center">
                    <a-icon
                        style="min-width: 24px"
                        class="mr-1"
                        icon="Calendar"
                    />
                    <span class="typo d-flex" style="gap: 0 0.25rem">
                        <span style="white-space: nowrap">
                            Od&nbsp;<strong>{{ startAt.split(',')[0] }}</strong>
                            {{ startAt.split(',')[1] }}
                        </span>
                        <span style="white-space: nowrap">
                            do&nbsp;<strong>{{ endAt.split(',')[0] }}</strong>
                            {{ endAt.split(',')[1] }}
                        </span>
                    </span>
                </div>
                <p class="pl-2 typo d-lg-none">
                    ({{ daysLeft.diff }} do końca promocji)
                </p>
            </div>
            <promotion-preview-heart
                :id="promotion.id"
                @click="favorite => toggleFavorites(promotion.id, favorite)"
            />
        </div>

        <div class="v-promo__image c-fig c-fig--promos mt-0">
            <img class="c-fig__img" :src="promoImg" />
        </div>

        <div class="v-promo__headright">
            <div class="v-promo__groupname mb-1" v-if="promoGroup">
                <h4 class="typo typo--gray typo--semi">
                    {{ promoGroup.name }}

                    <a-icon
                        v-if="promoGroup.acceptance === 'all'"
                        v-tooltip="
                            `<div class='p-2\'>
                                <h6 class=\'mb-1\'>Grupa zamknięta</h6>
                                <span>
                                    Wymaga aktywacji wszystkich promocji, które wchodzą w jej skład.
                                </span>
                            </div>`
                        "
                        class="ml-2 text-warning"
                        icon="PadlockDown"
                    />
                </h4>
            </div>

            <div class="v-promo__name">
                <h1 class="typo typo--name" v-text="promotionName" />
            </div>

            <div
                v-if="promoText.title"
                class="v-promo__category d-flex flex-column align-items-start mb-2"
            >
                <div
                    class="typo d-flex align-items-center cursor-pointer"
                    data-toggle="collapse"
                    aria-haspopup="true"
                    :data-target="`#promo-type-details`"
                    aria-expanded="false"
                    aria-controls="promo-type-details"
                    @click="showPromoTypeDetails = !showPromoTypeDetails"
                >
                    <a-icon icon="Info" class="text-dark mr-2"></a-icon>

                    <span>{{ promoType }}</span>

                    <a-icon
                        icon="Chevron"
                        :icon-config="{
                            up: showPromoTypeDetails,
                            down: !showPromoTypeDetails
                        }"
                    />
                </div>

                <div
                    class="collapse mt-2 mb-2 card px-3 py-2"
                    id="promo-type-details"
                    :class="{ show: showPromoTypeDetails }"
                >
                    <p class="card-text m-0" v-text="promoText.title" />

                    <p class="card-text m-0" v-text="promoText.details" />
                </div>
            </div>

            <div v-else class="v-promo__category mb-2">
                <span class="typo" v-text="promoType" />
            </div>

            <div class="v-promo__image c-fig c-fig--promos">
                <img
                    :src="promoImg"
                    class="c-fig__img"
                    width="178"
                    height="118"
                />
            </div>

            <div class="v-promo__brand">
                <div class="c-fig c-fig--logo">
                    <img
                        :src="promotion.company.type === 'Retailer::Company'
                            ? '/img/icons/ep/own_promo.png'
                            : promotion.company.logo.urls.small"
                        alt
                        class="c-fig__img"
                    />
                </div>
            </div>
        </div>

        <div class="v-promo__desktop-infographic">
            <infographic :infographic="promotion.infographic" />
        </div>
    </header>
</template>

<script>
import { PROMO_TYPES } from '@/utils/constants/promo-types'
import Infographic from '@/components/promotion/Infographic'
import PromotionPreviewHeart from '@/components/promotions/promotionsPreview/PromotionPreviewHeart'

export default {
    props: {
        promotion: {
            type: Object,
            required: true
        },
        productSets: {
            type: Array,
            default: () => []
        }
    },

    components: {
        Infographic,
        PromotionPreviewHeart
    },

    data() {
        return {
            showPromoTypeDetails: false
        }
    },

    computed: {
        startAt() {
            return this.$moment(this.promotion.start_at).format(
                'DD-MM-YYYY, HH:mm'
            )
        },

        endAt() {
            return this.$moment(this.promotion.end_at).format(
                'DD-MM-YYYY, HH:mm'
            )
        },

        daysLeft() {
            return this.$store.getters.getDaysLeft(this.promotion)
        },

        promoGroup() {
            const group = this.$store.getters.getPromoGroups[0]
            return {
                ...group,
                selectedPromoIds: [this.promotion.id]
            }
        },

        promoType() {
            return this.$store.getters.getPromoTypeByValue(this.promotion.type)
        },

        promoText() {
            const promoText = {
                title: '',
                details: ''
            }

            switch (this.promotion.type) {
            case PROMO_TYPES.PROMO_ITEM_FIXED_PRICE:
                promoText.title =
                        'Sprzedaj wymaganą liczbę sztuk produktu objętego promocją i zyskuj refundację za każdy sprzedany zestaw.'
                break
            case PROMO_TYPES.MIX1:
                promoText.title =
                        'Sprzedaj wymaganą liczbę sztuk dowolnych produktów objętych promocją i zyskuj Zwrot Kosztów za każdy sprzedany zestaw.'
                break
            case PROMO_TYPES.PERIODIC_PRICE_REDUCTION:
            case PROMO_TYPES.WEIGHT_PERIODIC_PRICE_REDUCTION:
                promoText.title =
                        'Sprzedaj promowany produkt, którego cena została czasowo zmieniona i otrzymuj Zwrot Kosztów za każdą sprzedaż.'
                promoText.details =
                        'Uwaga, cena produktów objętych promocją jest automatycznie aktualizowana na urządzeniach fiskalnych zgodnie z harmonogramem obowiązywania promocji.'
                break
            case PROMO_TYPES.REGULAR_PRICE:
            case PROMO_TYPES.WEIGHT_REGULAR_PRICE:
                promoText.title =
                        'Ustaw obowiązującą cenę regularną produktów poprzez aktywację tej oferty.'
                break
            case PROMO_TYPES.QUOTA:
                promoText.title =
                        'Sprzedawaj promowane produkty z określonym rabatem kwotowym dla kupującego.'
                break
            case PROMO_TYPES.ADVERTISEMENT:
                promoText.title =
                        'Wydruk reklamy w części niefiskalnej paragonu.'
                break
            case PROMO_TYPES.BONDED:
            case PROMO_TYPES.BONDED_QUOTA:
            case PROMO_TYPES.BONDED_PERCENTAGE:
                promoText.title =
                        'Sprzedaj wymaganą liczbę sztuk towarów z listy produktów wywołujących promocje oraz wymaganą liczbę sztuk z listy produktów rabatowanych. Sprzedaż musi być zrealizowana na pojedynczym paragonie.'
                break
            case PROMO_TYPES.SALES_TARGET:
                promoText.title = 'Sprzedaj wymaganą liczbę sztuk produktów objętych akcją i otrzymaj nagrodę.'
                break
            default:
                break
            }
            return promoText
        },

        promoImg() {
            if (this.promotion.type === PROMO_TYPES.ADVERTISEMENT) {
                return require('@/assets/img/promo-adv.png')
            } else {
                return (
                    this.promotion.logo?.urls.big ||
                    require('@/assets/logo-ep.svg')
                )
            }
        },

        isOwnPromo() {
            return (
                +this.promotion.promo_group_id ===
                +process.env.VUE_APP_OWN_GROUP_ID
            )
        },

        promotionName() {
            const { promotion } = this

            let name = promotion.name

            const set = promotion.type === PROMO_TYPES.MIX1 && this.productSets[0]

            if (this.isOwnPromo && set) {
                name += ` - w cenie ${this.$options.filters.currency(
                    set.price / +set.required_weight
                )}/szt. przy zakupie ${+set.required_weight} szt.`
            }

            return name
        }
    },

    methods: {
        toggleFavorites(promoId, favorite) {
            this.$store.dispatch('toggleFavorites', {
                promoId,
                favorite
            })
        }
    }
}
</script>
